import React, { ChangeEvent, FC, useState } from 'react'

import clsx from 'clsx'
import { AutorenewRounded } from '@mui/icons-material'
import { Camera } from 'lucide-react'

import FirebaseStorageService from '../../../../../services/storageService'

import FirestoreService from '../../../../../services/firestoreService'
import useToasterHelper from '../../../../../helpers/ToasterHelper'
import { handleImageError } from '../../../../../helpers/handleImageError'
import { generateName } from '../../../../../helpers/helpers'
import { getFloatPrice } from '../../../../../helpers/price'

import { RegistrationByDayModel } from '../../../../../models/registrations-by-day/registrationByDay.model'

import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../../../models/registrations-by-day/registrationByDay.interface'

import { useAppSelector } from '../../../../../store/hooks'
import { selectUserId } from '../../../../../store/user/userSlice'

import { IMAGE_CONSTS } from '../../../../../const/image-const'
import { MESSAGES_CONST } from '../../../../../const/messages-const'
import { CONST } from '../../../../../const/const'

interface ScoringRiderInfoProps {
  row: IRegistrationByDayInterface
  setRow: (data: IRegistrationByDayInterface) => void
  file: string | null
  setFile: (value: string | null) => void
  editable: boolean
  className?: string
  prizeMoney: string | number
  publishPrize: boolean
  currency: string
}
export const ScoringRiderInfo: FC<ScoringRiderInfoProps> = ({
  row,
  setRow,
  file,
  setFile,
  editable,
  className,
  publishPrize,
  prizeMoney,
  currency,
}) => {
  const toastFunctions = useToasterHelper()

  const userId = useAppSelector(selectUserId)

  const [uploadLoading, setUploadLoading] = useState<boolean>(false)

  async function fileChangedHandler(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files
    const file = files?.[0]

    if (!file) return toastFunctions.success({ message: MESSAGES_CONST.NO_FILE_SELECTED })

    setUploadLoading(true)

    const PATH = CONST.DATA.STORAGE.EVENTS.EVENT_SCORE.PREFIX

    try {
      const downloadUrl = await FirebaseStorageService.uploadFile(file, `${PATH}/${userId}`)

      const newData = new RegistrationByDayModel(row)

      newData.score = { ...newData.score, file: downloadUrl as string }
      setFile(downloadUrl as string)

      await FirestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.REGISTRATION_BY_DAY.NAME,
        newData.id,
        newData.toFirestore()
      )
      setRow(newData)

      toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCORE_UPLOAD })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      console.error('error: ', error)
    } finally {
      setUploadLoading(false)
    }
  }

  return (
    <div className={clsx('flex justify-between mb-6', className)}>
      <div>
        <div className="flex gap-3 mb-2">
          <img
            className="w-[45px] h-[45px] rounded-lg object-cover"
            src={
              row?.riderProfilePicture ? row.riderProfilePicture : IMAGE_CONSTS.PLACEHOLDERS.USER
            }
            alt=""
          />
          <div>
            <div className="opacity-50 text-sm">Rider • Back Number</div>
            <div>
              {row?.riderName} • {Number(row.backNumber) > 0 ? row.backNumber : 'N/A'}
            </div>
          </div>
        </div>

        {/* Horse Info */}
        <div className="flex gap-3">
          <img
            className="w-[45px] h-[45px] rounded-lg object-cover"
            src={row?.horseProfilePicture ?? ''}
            onError={(e) => handleImageError(e, generateName(row?.horseName || ''))}
            alt=""
          />
          <div>
            <div className="opacity-50 text-sm">Horse</div>
            <div>{row?.horseName ?? HORSE_NAME_DEFAULT}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end justify-between">
        {publishPrize && (
          <div className="flex items-center gap-2">
            <div className="text-right items-center">
              <div className="text-[14px] opacity-50">Prize Money</div>
              <span>
                {getFloatPrice(
                  `${currency}${(Number(prizeMoney || 0) * Number(row.score?.earning)) / 100}`
                )}
              </span>
            </div>
            <div className="flex items-center justify-center w-[45px] h-[45px] rounded-md bg-SeabiscuitMainThemeColor/5">
              <img src="/assets/og_icons/Money Bag-3.svg" className="w-6" alt="" />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="text-right items-center">
            <div className="text-[14px] opacity-50">File</div>
            <div>
              <label
                className={clsx(
                  (editable || file) && 'underline cursor-pointer hover:no-underline'
                )}
                onClick={(e) => {
                  if (file) {
                    if (!editable) {
                      e.preventDefault()
                      window.open(file, '_blank')
                    }
                  }
                }}
              >
                {uploadLoading ? (
                  <AutorenewRounded fontSize="small" className="animate-spin mx-auto" />
                ) : file ? (
                  editable ? (
                    'Update File'
                  ) : (
                    'View File'
                  )
                ) : editable ? (
                  'Add File'
                ) : (
                  'N/A'
                )}
                {editable && !uploadLoading && (
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => fileChangedHandler(e)}
                  />
                )}
              </label>
            </div>
          </div>

          <div className="flex items-center justify-center w-[45px] h-[45px] rounded-md bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor">
            <a target="_blank" href={file ?? ''} rel="noreferrer">
              <Camera className="w-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
